import React, {
  useEffect,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Box,
  Modal,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  TextField,
  Snackbar,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import EditNoteIcon from "@mui/icons-material/EditNote";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useDropzone } from "react-dropzone";
import api from "../../utils/api";
import { ThemeContext } from "../../utils/ThemeContext";

const EditArchitecturalRequest = forwardRef(
  ({ architecturalRequestDetails = {}, onUpdate }, ref) => {
    const { isDark } = React.useContext(ThemeContext);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [updatedStatus, setUpdatedStatus] = useState("pending");
    const [commentText, setCommentText] = useState("");
    const [commentType, setCommentType] = useState("internal");
    const [pendingCommentType, setPendingCommentType] = useState("internal");

    const [confirmExternalOpen, setConfirmExternalOpen] = useState(false);

    const [statuses, setStatuses] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);

    const [snackbar, setSnackbar] = useState({
      open: false,
      message: "",
      severity: "success",
    });

    const currentUserId = parseInt(
      JSON.parse(sessionStorage.getItem("user") || "{}")?.userId,
      10
    );

    useImperativeHandle(ref, () => ({
      openModal: () => setIsEditModalOpen(true),
    }));

    useEffect(() => {
      const fetchStatuses = async () => {
        try {
          const res = await api.get(`/statuses/architectural`);
          setStatuses(res.data || []);
        } catch (err) {
          console.error("Failed to fetch statuses:", err);
        }
      };
      fetchStatuses();
    }, []);

    useEffect(() => {
      setUpdatedStatus(architecturalRequestDetails?.status ?? "pending");
      setCommentText("");
      setSelectedFiles([]);
      setCommentType("internal");
    }, [architecturalRequestDetails]);

    const onDrop = useCallback((files) => {
      setSelectedFiles((prev) => [...prev, ...files]);
    }, []);

    const handleRemoveFile = (idx) => {
      setSelectedFiles((prev) => prev.filter((_, i) => i !== idx));
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
      multiple: true,
    });

    const handleCommentTypeChange = (value) => {
      if (value === "external") {
        setPendingCommentType(value);
        setConfirmExternalOpen(true);
      } else {
        setCommentType("internal");
      }
    };

    const confirmExternal = () => {
      setCommentType(pendingCommentType);
      setConfirmExternalOpen(false);
    };

    const cancelExternal = () => {
      setCommentType("internal");
      setConfirmExternalOpen(false);
    };

    const handleSubmit = async () => {
      try {
        setIsSaving(true);

        const formData = new FormData();
        formData.append("status", updatedStatus);
        formData.append("reviewed_by", currentUserId);
        formData.append("user_id", currentUserId);
        formData.append("is_internal", commentType === "internal" ? 1 : 0);

        if (commentText.trim()) {
          formData.append("comment", commentText);
        }

        await api.put(
          `/architectural-requests/${architecturalRequestDetails.id}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        if (selectedFiles.length > 0) {
          const fileData = new FormData();
          fileData.append("request_id", architecturalRequestDetails.id);
          fileData.append("uploaded_by", currentUserId);

          selectedFiles.forEach((file) => {
            fileData.append("upload_urls", file);
          });

          await api.post("/request-files", fileData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }

        setSnackbar({
          open: true,
          message: "Architectural request updated successfully!",
          severity: "success",
        });

        onUpdate?.();
        setIsEditModalOpen(false);
      } catch (err) {
        console.error("Error updating:", err);

        setSnackbar({
          open: true,
          message: "Failed to update request",
          severity: "error",
        });
      } finally {
        setIsSaving(false);
      }
    };

    if (!architecturalRequestDetails?.id) return null;

    const modalCardSx = {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: { xs: "92%", sm: 520 },
      maxHeight: "90vh",
      overflow: "auto",
      backgroundColor: isDark ? "#1a2c46" : "rgba(255,255,255,0.98)",
      borderRadius: "18px",
      border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(47,99,173,0.08)",
      boxShadow: isDark ? "0 20px 60px rgba(0,0,0,0.55)" : "0 20px 60px rgba(0,0,0,0.22)",
    };

    const sectionHeaderSx = {
      px: { xs: 2, sm: 2.5, md: 3 },
      pt: 2.5,
      pb: 1.5,
      borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(47,99,173,0.08)",
      backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(47,99,173,0.02)",
    };

    const contentWrapSx = {
      p: { xs: 2, sm: 2.5, md: 3 },
    };

    const textFieldSx = {
      "& .MuiOutlinedInput-root": {
        backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.96)",
        borderRadius: "10px",
      },
    };

    const primaryButtonSx = {
      backgroundColor: "#2f63ad",
      color: "white",
      fontWeight: 700,
      borderRadius: "10px",
      textTransform: "none",
      px: 2.25,
      boxShadow: "none",
      "&:hover": {
        backgroundColor: "#1f4f94",
        boxShadow: "none",
      },
    };

    const secondaryButtonSx = {
      borderRadius: "10px",
      textTransform: "none",
      fontWeight: 600,
      px: 2,
      borderColor: isDark ? "rgba(255,255,255,0.22)" : "rgba(47,99,173,0.18)",
      color: isDark ? "#b8c9de" : "#223142",
      "&:hover": {
        borderColor: isDark ? "#7ab3f0" : "#2f63ad",
        color: isDark ? "#dde5f0" : "#2f63ad",
        backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(47,99,173,0.04)",
      },
    };

    const dialogPaperSx = {
      borderRadius: "18px",
      backgroundColor: isDark ? "#1a2c46" : "rgba(255,255,255,0.98)",
      boxShadow: isDark ? "0 20px 60px rgba(0,0,0,0.55)" : "0 20px 60px rgba(0,0,0,0.22)",
      border: isDark ? "1px solid rgba(255,255,255,0.08)" : "none",
    };

    return (
      <>
        <Modal open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
          <Box sx={modalCardSx}>
            <Box sx={sectionHeaderSx}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <EditNoteIcon sx={{ color: "#2f63ad" }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: isDark ? "#dde5f0" : "#223142" }}>
                  Edit Architectural Request
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ color: isDark ? "#8a9ab8" : "#6b778c" }}>
                Update the request status, add comments, and attach supporting files.
              </Typography>
            </Box>

            <Box sx={contentWrapSx}>
              <FormControl fullWidth sx={{ mb: 2.25 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={updatedStatus}
                  onChange={(e) => setUpdatedStatus(e.target.value)}
                  sx={textFieldSx}
                >
                  {statuses.map((s) => (
                    <MenuItem key={s.id} value={s.statusname}>
                      {s.statusname}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Add Comment"
                multiline
                rows={4}
                sx={{ ...textFieldSx, mb: 2.25 }}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
              />

              <FormControl fullWidth sx={{ mb: 2.25 }}>
                <InputLabel>Comment Type</InputLabel>
                <Select
                  value={commentType}
                  label="Comment Type"
                  onChange={(e) => handleCommentTypeChange(e.target.value)}
                  sx={textFieldSx}
                >
                  <MenuItem value="external">External</MenuItem>
                  <MenuItem value="internal">Internal</MenuItem>
                </Select>
              </FormControl>

              <Divider sx={{ mb: 2.25, borderColor: "rgba(47,99,173,0.08)" }} />

              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 600, color: isDark ? "#dde5f0" : "#223142" }}
              >
                Attach Files
              </Typography>

              <Paper
                variant="outlined"
                {...getRootProps()}
                sx={{
                  p: 2.5,
                  border: isDark ? "2px dashed rgba(122,179,240,0.35)" : "2px dashed #90caf9",
                  borderRadius: "14px",
                  textAlign: "center",
                  cursor: "pointer",
                  backgroundColor: isDragActive
                    ? (isDark ? "rgba(122,179,240,0.1)" : "#e3f2fd")
                    : (isDark ? "rgba(255,255,255,0.03)" : "#fafafa"),
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: isDark ? "rgba(122,179,240,0.08)" : "#f5faff",
                    borderColor: isDark ? "#7ab3f0" : "#1976d2",
                  },
                }}
              >
                <input {...getInputProps()} />
                <CloudUploadIcon sx={{ fontSize: 40, color: "#1976d2", mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {isDragActive
                    ? "Drop files here..."
                    : "Drag & drop files here, or click to select"}
                </Typography>
              </Paper>

              {selectedFiles.length > 0 && (
                <List
                  dense
                  sx={{
                    mt: 1.5,
                    maxHeight: 150,
                    overflowY: "auto",
                    border: "1px solid rgba(47,99,173,0.08)",
                    borderRadius: "12px",
                    backgroundColor: "rgba(47,99,173,0.02)",
                    px: 1,
                  }}
                >
                  {selectedFiles.map((file, idx) => (
                    <ListItem
                      key={`${file.name}-${idx}`}
                      sx={{
                        borderBottom:
                          idx !== selectedFiles.length - 1
                            ? "1px solid rgba(47,99,173,0.08)"
                            : "none",
                      }}
                    >
                      <ListItemText
                        primary={file.name}
                        primaryTypographyProps={{
                          noWrap: true,
                          sx: { color: "#223142", fontWeight: 500 },
                        }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton onClick={() => handleRemoveFile(idx)} size="small">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1.25,
                  mt: 3,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => setIsEditModalOpen(false)}
                  sx={secondaryButtonSx}
                  disabled={isSaving}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={isSaving}
                  sx={primaryButtonSx}
                >
                  {isSaving ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    "Submit Changes"
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>

        <Dialog
          open={confirmExternalOpen}
          onClose={cancelExternal}
          PaperProps={{ sx: dialogPaperSx }}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontWeight: 700,
              color: "#223142",
              pt: 3,
            }}
          >
            <WarningAmberIcon sx={{ color: "#ed6c02" }} />
            Send to Homeowner?
          </DialogTitle>

          <DialogContent sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              External comments will be emailed to the homeowner. Continue?
            </Typography>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3, gap: 1.25 }}>
            <Button onClick={cancelExternal} variant="outlined" sx={secondaryButtonSx}>
              Cancel
            </Button>
            <Button onClick={confirmExternal} variant="contained" sx={primaryButtonSx}>
              Yes, Continue
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 3 }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </>
    );
  }
);

export default EditArchitecturalRequest;
